/**
 * Two-state segmented control at the top of Settings → LLM: do these
 * settings apply tenant-wide (default) or are they personal overrides
 * just for the current user?
 */
import type { LlmScope } from '@/types'
import { Users, User } from 'lucide-react'

interface Props {
  value: LlmScope
  onChange: (scope: LlmScope) => void
  disabled?: boolean
}

export default function ScopeToggle({ value, onChange, disabled }: Props) {
  const opt = (id: LlmScope, label: string, hint: string, Icon: React.ComponentType<{ size?: number }>) => {
    const selected = value === id
    return (
      <button
        type="button"
        onClick={() => !disabled && onChange(id)}
        disabled={disabled}
        style={{
          flex: 1,
          padding: '14px 16px',
          background: selected ? 'var(--color-bg-elevated)' : 'transparent',
          border: selected ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
          borderRadius: 8,
          color: selected ? 'var(--color-accent)' : 'var(--color-text-primary)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ paddingTop: 2 }}>
          <Icon size={18} />
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{label}</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
            {hint}
          </div>
        </div>
      </button>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 12 }}>
        {opt('tenant', 'Tenant-wide', 'Shared by every member of your organization', Users)}
        {opt('user', 'Personal', 'Your own overrides — only affect you', User)}
      </div>
      <div
        style={{
          marginTop: 8,
          fontSize: 13,
          color: 'var(--color-text-secondary)',
        }}
      >
        {value === 'tenant'
          ? 'Saving here updates the LLM configuration for every user in your tenant.'
          : 'Saving here creates a personal override. The tenant configuration stays unchanged.'}
      </div>
    </div>
  )
}
